import { Colors, ConfirmDialog, Icon, PageContainer } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
    createQuestionnaireSchema,
    questionSchema,
    type QuestionnaireFormData,
} from '../../shared/QuestionnaireCreation/validation';

import type { Evaluation360FormNavigatorParams } from '@/navigation/Evaluation360/Evaluation360FormNavigator';
import type { QuestionActionItemsProps } from '@/pages/shared/QuestionnaireCreation/types';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewQuestionnaire } from '@/types/review/ReviewQuestionnaire';
import type { DraggableItem, OptionProps } from '@amelio/ui-library';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetUsersByIds } from '@/api/directory';
import {
    useCreateReviewQuestion,
    useDeleteReviewQuestion,
    useUpdateReviewQuestion,
} from '@/api/review-services/review-question';
import { useUpdateReviewQuestionnaire } from '@/api/review-services/review-questionnaire';
import { lastSavedAtAtom, questionnaireCreationOnNextAtom } from '@/atoms/evaluation360';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import {
    QuestionnaireFilters,
    type AdvancedOptionFilterType,
} from '@/constants/questionnaire/Filters';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { type QuestionTypeValue } from '@/constants/QuestionType';
import {
    useAmelioTranslations,
    useNavigate,
    useNavigateInNewPage,
    useRegisterPageAction,
} from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useToggleQuestionStatus } from '@/hooks/useToggleQuestionStatus';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import { getReviewFormRouteName, getCycleRouteName } from '@/navigation/utils';
import QuestionnaireCreation from '@/pages/shared/QuestionnaireCreation/components/QuestionnaireCreation';
import { QuestionnaireCreationSkeleton } from '@/pages/shared/QuestionnaireCreation/components/QuestionnaireCreationSkeleton';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { getAllCycleInstanceAssignedUserIds, getStepPath } from '@/utils/cycleInstance';
import { getLocalizedText } from '@/utils/localization';
import { createEmptyQuestion, filterQuestions } from '@/utils/question';
import { getReviewees } from '@/utils/reviewAudience';
import { validationMessageKey } from '@/utils/validationMessage';

export const QuestionnaireCreationPage = () => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const { handleError } = useError();
    const navigate = useNavigate();

    const navigateInNewPage = useNavigateInNewPage();
    const { cycleInstanceId } = useParams<Evaluation360FormNavigatorParams>();
    const setLastSavedAt = useSetAtom(lastSavedAtAtom);

    const {
        data: cycleInstance,
        isLoading: isFetchingCycleInstance,
        isError: isFetchingCycleInstanceError,
    } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const userIds: string[] = useMemo(() => {
        if (!cycleInstance) {
            return [];
        }

        return Array.from(getAllCycleInstanceAssignedUserIds([cycleInstance]));
    }, [cycleInstance]);

    const {
        data: users,
        isLoading: isUsersLoading,
        isError: isUsersError,
    } = useGetUsersByIds({
        userIds,
        identifier: 'questionnaire-creation',
    });

    const { mutateAsync: updateQuestionnaire, isPending: isPendingUpdateQuestionnaire } =
        useUpdateReviewQuestionnaire();
    const { mutateAsync: updateQuestion, isPending: isPendingUpdateQuestion } =
        useUpdateReviewQuestion();
    const { mutateAsync: createQuestion, isPending: isPendingCreateQuestion } =
        useCreateReviewQuestion();
    const { mutateAsync: deleteQuestion, isPending: isPendingDeleteQuestion } =
        useDeleteReviewQuestion();

    const [questionToDelete, setQuestionToDelete] = useState<ReviewQuestion | undefined>(undefined);
    const [isAddingQuestion, setIsAddingQuestion] = useState<boolean>(false);
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
    const questionSnapshotRef = useRef<ReviewQuestion | null>(null);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [questionTypeFilters, setQuestionTypeFilters] = useState<QuestionTypeValue[]>([]);
    const [advancedOptionsFilters, setAdvancedOptionsFilters] = useState<
        AdvancedOptionFilterType[]
    >([]);
    const [tempQuestion, setTempQuestion] = useState<ReviewQuestion>(() =>
        createEmptyQuestion(defaultLanguage),
    );

    const questionnaireSchema = useMemo(
        () => createQuestionnaireSchema(defaultLanguage),
        [defaultLanguage],
    );

    const { watch, setValue, reset } = useForm<QuestionnaireFormData>({
        resolver: zodResolver(questionnaireSchema),
        defaultValues: { id: '', title: {}, reviewTemplateId: null, reviewQuestions: [] },
    });
    const formQuestions = watch('reviewQuestions') || [];
    const formTitle = watch('title');

    const currentQuestion = useMemo(() => {
        if (selectedQuestionId) {
            return formQuestions.find((question) => question.id === selectedQuestionId);
        }

        return tempQuestion;
    }, [selectedQuestionId, formQuestions, tempQuestion]);

    const questionList = useMemo(
        () => filterQuestions({ formQuestions, questionTypeFilters, advancedOptionsFilters }),
        [formQuestions, questionTypeFilters, advancedOptionsFilters],
    );

    const handleFilterChange = (filter: string) => {
        const isSelected = selectedFilters.includes(filter);

        if (isSelected) {
            setSelectedFilters((prev) => prev.filter((f) => f !== filter));

            if (filter === QuestionnaireFilters.QUESTION_TYPE) {
                setQuestionTypeFilters([]);
            } else if (filter === QuestionnaireFilters.ADVANCED_OPTIONS) {
                setAdvancedOptionsFilters([]);
            }
        } else {
            setSelectedFilters((prev) => [...prev, filter]);
        }
    };

    const onQuestionTypeFilterChange = (filter: QuestionTypeValue) => {
        setQuestionTypeFilters((prev) => {
            if (prev.includes(filter)) {
                return prev.filter((f) => f !== filter);
            }

            return [...prev, filter];
        });
    };

    const onAdvancedOptionsFilterChange = (filter: AdvancedOptionFilterType) => {
        setAdvancedOptionsFilters((prev) => {
            if (prev.includes(filter)) {
                return prev.filter((f) => f !== filter);
            }

            return [...prev, filter];
        });
    };

    const validateQuestionnaire = useCallback(() => {
        const result = createQuestionnaireSchema(defaultLanguage).safeParse({
            id: cycleInstance?.reviewQuestionnaire?.id || '',
            title: formTitle,
            reviewTemplateId: cycleInstance?.reviewQuestionnaire?.reviewTemplateId ?? null,
            reviewQuestions: formQuestions?.length > 0 ? formQuestions : [],
        });

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return false;
        }

        return true;
    }, [cycleInstance, formTitle, formQuestions, t]);

    const handleTitleActionButton = async () => {
        if (isEditingTitle) {
            if (!validateQuestionnaire()) {
                return;
            }

            try {
                const updatedQuestionnaire: ReviewQuestionnaire = {
                    id: cycleInstance?.reviewQuestionnaire?.id || '',
                    title: formTitle,
                    reviewTemplateId: cycleInstance?.reviewQuestionnaire?.reviewTemplateId ?? null,
                    reviewQuestions: formQuestions?.length > 0 ? formQuestions : [],
                    cycleInstanceId: cycleInstance?.id || '',
                };

                await updateQuestionnaire(updatedQuestionnaire);
                setLastSavedAt(new Date());

                onCancelEdit();
            } catch (error) {
                handleError({ error });
            }
            setIsEditingTitle(false);
        } else {
            setIsEditingTitle(true);
        }
    };

    const onTitleChange = useCallback(
        async (title: string) => {
            setValue('title', { ...formTitle, [defaultLanguage]: title });
        },
        [setValue, formTitle, defaultLanguage],
    );

    const onAddQuestionClick = useCallback(() => {
        setIsAddingQuestion(true);
        setTempQuestion(createEmptyQuestion(defaultLanguage));
    }, [defaultLanguage]);

    const onEditQuestion = useCallback(
        (question: ReviewQuestion) => {
            // First, restore any unsaved changes from the previously selected question
            if (selectedQuestionId && questionSnapshotRef.current) {
                const restoredQuestions = formQuestions.map((questionItem) =>
                    questionItem.id === selectedQuestionId
                        ? questionSnapshotRef.current!
                        : questionItem,
                );
                setValue('reviewQuestions', restoredQuestions);
            }

            // Then, set up the new question for editing
            questionSnapshotRef.current = formQuestions.find((q) => q.id === question.id) ?? null;
            setSelectedQuestionId(question.id || null);
        },
        [formQuestions, selectedQuestionId, setValue],
    );

    const onCancelEdit = useCallback(() => {
        if (selectedQuestionId && questionSnapshotRef.current) {
            const restoredQuestions = formQuestions.map((q) =>
                q.id === selectedQuestionId ? questionSnapshotRef.current! : q,
            );
            setValue('reviewQuestions', restoredQuestions);
        }
        setIsAddingQuestion(false);
        setSelectedQuestionId(null);
        setTempQuestion(createEmptyQuestion(defaultLanguage));
        questionSnapshotRef.current = null;
    }, [defaultLanguage, selectedQuestionId, formQuestions, setValue]);

    const onDeleteQuestion = useCallback((question: ReviewQuestion) => {
        if (!question.id) {
            return;
        }

        setQuestionToDelete(question);
    }, []);

    const onConfirmDeleteQuestion = useCallback(async () => {
        if (!questionToDelete?.id) {
            return;
        }

        const updatedQuestions = formQuestions.filter((q) => q.id !== questionToDelete.id);
        setValue('reviewQuestions', updatedQuestions);

        try {
            await deleteQuestion(questionToDelete.id);
            setLastSavedAt(new Date());

            showSuccessToast({
                message: t('evaluation360Questionnaire.message.questionDeleted'),
            });
        } catch (error) {
            handleError({ error });
        } finally {
            setQuestionToDelete(undefined);
        }
    }, [questionToDelete, formQuestions, deleteQuestion, setValue, handleError, setLastSavedAt, t]);

    const onSelectTemplateClick = () => {
        const routeName = getReviewFormRouteName(cycleInstance?.reviewType);

        navigate({
            path: `${routeName}/${getStepPath(CycleInstanceSteps.SELECT_TEMPLATE)}/${cycleInstanceId}`,
        });
    };

    const onTranslateClick = useCallback(() => {
        navigate({
            path: getCycleRouteName(cycleInstance?.reviewType),
            state: {
                cycleInstanceId,
                step: 'questionnaire-creation',
            },
        });
    }, [navigate, cycleInstance?.reviewType, cycleInstanceId]);

    const onPreviewClick = () => {
        if (!formQuestions.some((question) => question.status === ReviewQuestionStatus.ACTIVE)) {
            return showWarningToast({
                message: t('evaluation360Questionnaire.message.noQuestions'),
            });
        }

        const reviewees = getReviewees({
            reviewAudience: cycleInstance?.reviewAudience,
            users,
        });

        const firstReviewee = reviewees.length > 0 ? reviewees[0] : undefined;

        navigateInNewPage({
            path: RouteName.SurveyAnswer,
            searchParams: {
                questionnaireId: cycleInstance?.reviewQuestionnaire?.id,
                revieweeId: firstReviewee?.id,
                reviewType: cycleInstance?.reviewType,
            },
        });
    };

    const onReorderQuestions = useCallback(
        (items: DraggableItem<ReviewQuestion>[]) => {
            const reorderedQuestions = items.map((item, index) => ({
                ...item.data,
                sortOrder: index,
            }));

            setValue('reviewQuestions', reorderedQuestions);

            const updatedQuestionnaire: ReviewQuestionnaire = {
                id: cycleInstance?.reviewQuestionnaire?.id || '',
                title: formTitle,
                reviewTemplateId: cycleInstance?.reviewQuestionnaire?.reviewTemplateId ?? null,
                reviewQuestions: reorderedQuestions,
                cycleInstanceId: cycleInstance?.id || '',
            };

            updateQuestionnaire(updatedQuestionnaire).then(() => {
                setLastSavedAt(new Date());
            });
        },
        [cycleInstance, formTitle, setValue, updateQuestionnaire, setLastSavedAt],
    );

    const onQuestionChange = useCallback(
        (question: ReviewQuestion) => {
            if (selectedQuestionId) {
                const updatedQuestions = formQuestions.map((q) =>
                    q.id === selectedQuestionId ? question : q,
                );
                setValue('reviewQuestions', updatedQuestions);
            } else {
                setTempQuestion(question);
            }
        },
        [formQuestions, selectedQuestionId, setValue],
    );

    const onUpdateQuestion = async (question: ReviewQuestion) => {
        const result = questionSchema.safeParse(question);

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }

        try {
            await updateQuestion(result.data);

            const updatedQuestions = formQuestions.map((existingQuestion) =>
                existingQuestion.id === result.data.id ? result.data : existingQuestion,
            );

            setValue('reviewQuestions', updatedQuestions);
            setLastSavedAt(new Date());
            onCancelEdit();

            showSuccessToast({ message: t('evaluation360Questionnaire.message.questionUpdated') });
        } catch (error) {
            handleError({ error });
        }
    };

    const onCreateQuestion = async (question: ReviewQuestion) => {
        const result = questionSchema.safeParse(question);

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }

        const updatedQuestion = {
            ...result.data,
            reviewQuestionnaireId: cycleInstance?.reviewQuestionnaire?.id,
            sortOrder: formQuestions.length,
        };

        try {
            const response: ReviewQuestion = await createQuestion(updatedQuestion);

            setValue('reviewQuestions', [...formQuestions, { ...result.data, id: response.id }]);
            setLastSavedAt(new Date());
            onCancelEdit();

            showSuccessToast({ message: t('evaluation360Questionnaire.message.questionCreated') });
        } catch (error) {
            handleError({ error });
        }
    };

    const { onToggleQuestionStatus } = useToggleQuestionStatus({
        updateQuestion,
        formQuestions,
        onSuccess: (updatedQuestions) => {
            setValue('reviewQuestions', updatedQuestions);
            setLastSavedAt(new Date());
        },
    });

    const onNext = async () => {
        if (!validateQuestionnaire()) {
            return false;
        }

        if (!formQuestions.some((question) => question.status === ReviewQuestionStatus.ACTIVE)) {
            showWarningToast({
                message: t('evaluation360Questionnaire.message.noQuestions'),
            });

            return false;
        }
        try {
            const updatedQuestionnaire: ReviewQuestionnaire = {
                id: cycleInstance?.reviewQuestionnaire?.id || '',
                title: formTitle,
                reviewTemplateId: cycleInstance?.reviewQuestionnaire?.reviewTemplateId ?? null,
                reviewQuestions: formQuestions,
                cycleInstanceId: cycleInstance?.id || '',
            };

            await updateQuestionnaire(updatedQuestionnaire);

            return true;
        } catch (error) {
            handleError({ error });
            return false;
        }
    };

    const filterOptions: OptionProps[] = [
        {
            label: t('evaluation360Questionnaire.questionForm.filters.advancedOptions'),
            value: QuestionnaireFilters.ADVANCED_OPTIONS,
        },
        {
            label: t('evaluation360Questionnaire.questionForm.filters.questionType'),
            value: QuestionnaireFilters.QUESTION_TYPE,
        },
    ];

    const questionActionItems: QuestionActionItemsProps[] = [
        {
            icon: <Icon name={'edit-edit-2'} />,
            label: t('common.edit'),
            onClick: (question: ReviewQuestion) => onEditQuestion(question),
        },
        {
            icon: (
                <Icon
                    name={'essential-trash'}
                    color={Colors.ERROR}
                />
            ),
            label: t('common.delete'),
            danger: true,
            onClick: (question: ReviewQuestion) => onDeleteQuestion(question),
        },
    ];

    useEffect(() => {
        if (cycleInstance?.reviewQuestionnaire) {
            reset({
                id: cycleInstance?.reviewQuestionnaire.id,
                title: cycleInstance?.reviewQuestionnaire.title,
                reviewTemplateId: cycleInstance?.reviewQuestionnaire.reviewTemplateId,
                reviewQuestions: cycleInstance?.reviewQuestionnaire.reviewQuestions || [],
            });

            if (cycleInstance.reviewQuestionnaire.updatedAt) {
                setLastSavedAt(new Date(cycleInstance.reviewQuestionnaire.updatedAt));
            }
        }
    }, [cycleInstance, reset, setLastSavedAt]);

    useFetchErrorsHandler({
        errors: [isFetchingCycleInstanceError, isUsersError],
    });

    useRegisterPageAction(questionnaireCreationOnNextAtom, onNext);

    if (isFetchingCycleInstance || isUsersLoading) {
        return (
            <Skeleton>
                <PageContainer className={'flex w-full flex-1 flex-col gap-6'}>
                    <QuestionnaireCreationSkeleton />
                </PageContainer>
            </Skeleton>
        );
    }

    return (
        <>
            <QuestionnaireCreation
                reviewees={getReviewees({
                    reviewAudience: cycleInstance?.reviewAudience,
                    users,
                })}
                filterOptions={filterOptions}
                selectedFilters={selectedFilters}
                advancedOptionsFilters={advancedOptionsFilters}
                questionTypeFilters={questionTypeFilters}
                isAddingQuestion={isAddingQuestion}
                questions={questionList}
                selectedQuestionId={selectedQuestionId}
                currentQuestion={currentQuestion}
                questionActionItems={questionActionItems}
                onRowClick={onEditQuestion}
                isEditingTitle={isEditingTitle}
                title={getLocalizedText(formTitle)}
                isPendingUpdateQuestion={isPendingUpdateQuestion}
                isPendingUpdateQuestionnaire={isPendingUpdateQuestionnaire}
                isPendingCreateQuestion={isPendingCreateQuestion}
                isPendingDeleteQuestion={isPendingDeleteQuestion}
                setIsEditingTitle={handleTitleActionButton}
                onCancelTitle={() => setIsEditingTitle(false)}
                handleFilterChange={handleFilterChange}
                onTitleChange={onTitleChange}
                onQuestionTypeFilterChange={onQuestionTypeFilterChange}
                onAdvancedOptionsFilterChange={onAdvancedOptionsFilterChange}
                onPreviewClick={onPreviewClick}
                onAddQuestionClick={onAddQuestionClick}
                onDeleteQuestion={onDeleteQuestion}
                onCancelEdit={onCancelEdit}
                onQuestionChange={onQuestionChange}
                onReorderQuestions={onReorderQuestions}
                onUpdateQuestion={onUpdateQuestion}
                onCreateQuestion={onCreateQuestion}
                onToggleQuestionStatus={onToggleQuestionStatus}
                onSelectTemplateClick={onSelectTemplateClick}
                onTranslateClick={onTranslateClick}
            />

            <ConfirmDialog
                isOpen={Boolean(questionToDelete)}
                title={t('common.attention')}
                message={t('common.deleteQuestionMessage')}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={onConfirmDeleteQuestion}
                onCancel={() => setQuestionToDelete(undefined)}
                isLoading={isPendingDeleteQuestion}
                isDestructive
            />
        </>
    );
};
